import { readonly, ref } from 'vue';

import { filterObject } from '@/common/utilities';
import { useAtomic } from '@/use/useAtomic';
import { useBotUser } from '@/stores/useBotUser';
import { PleasedApi } from '@/pleased-sdk/PleasedApi';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import * as BridgeEvents from '@/common/bridge/modern-bridge/bridge-events';

import type { TicketCreateRequest } from '../cf-v1/usePleasedSupport.types';

export function usePleasedTicket() {
  const ticket = ref<null | PleasedApi.Model.TicketDetail>();
  const [createTicket, isCreatingTicket, errorCreatingTicket] = useAtomic(
    async (request: TicketCreateRequest['payload']) => {
      const { user } = useBotUser();

      $pr.openRequestClicked();

      const {
        subject,
        description,
        userSelections,
        chatbotSessionId,
        aiSessionId,
        aiVoiceChatSessionId,
        tags: requestTags,
        autoAddTags,
        recipientAddress,
        ticketFields,
        categoryId,
        transactionId,
        chatTriggerId,
      } = request;

      // Match ContactOptions.vue handleOpenRequest: `tags` is the filtered auto-add list only (no chatTags merge).
      const tags = requestTags ?? autoAddTags ?? [];
      const userLogs = $pr.getLogs();

      const newTicket = await PleasedApi.createTicket(
        filterObject({
          subject,
          description,
          recipientAddress: recipientAddress ?? user.email ?? '',
          ticketFields: ticketFields ?? [],
          userSelections,
          userLogs,
          tags,
          chatbotSessionId,
          aiSessionId,
          aiVoiceChatSessionId,
          categoryId,
          transactionId,
          chatTriggerId,
        })
      );

      $pr.reset();
      ticket.value = newTicket;

      BridgeEvents.emitOpenRequestCreated(newTicket, { description, chatbotSessionId });

      return newTicket;
    }
  );

  function resetError() {
    errorCreatingTicket.value = '';
  }

  function reset() {
    ticket.value = null;
    resetError();
  }

  return { createTicket, isCreatingTicket, errorCreatingTicket, ticket: readonly(ticket), resetError, reset };
}
